import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { useCreateMemberMutation, useUpdateMemberMutation } from '../store/rosterApi'
import { showErrorNotification } from 'Shared/helpers'

const t = (key, opts) => (!window.I18n ? key : window.I18n.t(key, opts))

const fieldId = (name) => `member-form-${name}`

// gender starts empty so saving never writes a gender the golfer did not have.
const EMPTY_FORM = { first_name: '', last_name: '', gender: '', email: '', ghin: '', usga_index: '', phone: '' }
const GENDERS = [ [ 'M', 'gender_male' ], [ 'F', 'gender_female' ], [ 'unknown', 'gender_unknown' ] ]
const MAX_USGA_INDEX = 99.9

const validateUsgaIndex = (val) => {
  if (!val || val.trim() === '') return null
  const numeric = parseFloat(val.trim().replace(/^[+\-p]/i, '').replace(/[^\d.]/g, ''))
  if (isNaN(numeric)) return null
  if (numeric > MAX_USGA_INDEX) return t('customer_groups.groups.members.member_modal.usga_index_out_of_range', { max: MAX_USGA_INDEX })
  return null
}

const MemberFormModal = ({ isOpen, onClose, member, phoneFieldName, handicapProvider }) => {
  const isEdit = Boolean(member)
  const firstFieldRef = useRef(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [usgaIndexError, setUsgaIndexError] = useState(null)
  const [createMember, { isLoading: isCreating }] = useCreateMemberMutation()
  const [updateMember, { isLoading: isUpdating }] = useUpdateMemberMutation()
  const isLoading = isCreating || isUpdating

  useEffect(() => {
    if (!isOpen) return
    setUsgaIndexError(null)
    setForm(isEdit ? Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [ key, member[key] || '' ])) : EMPTY_FORM)
    // A form popup starts on its first field; without this focus stays behind the overlay.
    firstFieldRef.current?.focus()
  }, [isOpen, member])

  if (!isOpen) return null

  const set = (field) => (e) => {
    const value = e.target.value
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleUsgaIndexChange = (e) => {
    setUsgaIndexError(null)
    set('usga_index')(e)
  }

  const textField = (name, label, inputProps = {}) => (
    <div className='form-group'>
      <label className='control-label' htmlFor={fieldId(name)}>{label}</label>
      <input
        name={name}
        id={fieldId(name)}
        className='form-control'
        value={form[name]}
        onChange={set(name)}
        {...inputProps}
      />
    </div>
  )

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const hiError = validateUsgaIndex(form.usga_index)
    if (hiError) {
      setUsgaIndexError(hiError)
      return
    }
    const payload = { ...form }
    const fallback = t(`customer_groups.groups.members.member_modal.${isEdit ? 'update_error' : 'create_error'}`)
    try {
      if (isEdit) {
        await updateMember({ memberId: member.id, ...payload }).unwrap()
      } else {
        await createMember(payload).unwrap()
      }
      onClose()
    } catch (error) {
      showErrorNotification(error?.error || error?.message || fallback)
    }
  }

  const modal = (
    <div className='member-form-overlay' onClick={onClose}>
      <div
        className='member-form-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='member-form-title'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      >
        <div className='member-form-modal__header'>
          <h4 className='member-form-modal__title' id='member-form-title'>
            {t(`customer_groups.groups.members.member_modal.${isEdit ? 'edit_title' : 'add_title'}`)}
          </h4>
          <button
            type='button'
            className='member-form-modal__close'
            onClick={onClose}
            aria-label={t('customer_groups.groups.members.member_modal.close')}
          >
            <span aria-hidden='true'>&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete='off'>
          <div className='member-form-modal__body'>
            <div className='row'>
              <div className='col-xs-6'>
                {textField('first_name', t('customer_groups.groups.leaders.form.first_name'), { ref: firstFieldRef, maxLength: 255 })}
              </div>
              <div className='col-xs-6'>
                {textField('last_name', (
                  <>
                    {t('customer_groups.groups.leaders.form.last_name')}
                    {' '}<span className='member-form-modal__required'>*</span>
                  </>
                ), { required: true, maxLength: 255 })}
              </div>
            </div>

            <div className='row'>
              <div className='col-xs-6'>
                {textField('email', t('customer_groups.groups.leaders.form.email'), { type: 'email', maxLength: 255 })}
              </div>
              <div className='col-xs-6'>
                {textField('phone', phoneFieldName)}
              </div>
            </div>

            <div className='row'>
              <div className='col-xs-6'>
                {textField('ghin', handicapProvider || t('customer_groups.groups.members.columns.ghin'))}
              </div>
              <div className='col-xs-6'>
                <div className={`form-group${usgaIndexError ? ' has-error' : ''}`}>
                  <label className='control-label' htmlFor={fieldId('usga_index')}>
                    {t('customer_groups.groups.members.columns.handicap_index')}
                  </label>
                  <input
                    name='usga_index'
                    id={fieldId('usga_index')}
                    type='text'
                    className='form-control'
                    placeholder={t('customer_groups.groups.members.member_modal.usga_index_placeholder')}
                    value={form.usga_index}
                    onChange={handleUsgaIndexChange}
                  />
                  {usgaIndexError && (
                    <span className='help-block'>{usgaIndexError}</span>
                  )}
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-xs-12'>
                <div className='form-group'>
                  <label className='control-label'>
                    {t('customer_groups.groups.members.columns.gender')}
                  </label>
                  <div className='gender_inputs add-padding-top-5'>
                    {GENDERS.map(([ value, labelKey ]) => (
                      <label key={value} className='icheck-label add-margin-right-15 pull-left'>
                        <div className={`iradio_square-orange icheck-item${form.gender === value ? ' checked' : ''}`}>
                          <input
                            className='i-checks icheck-input'
                            type='radio'
                            name='gender'
                            value={value}
                            checked={form.gender === value}
                            onChange={set('gender')}
                          />
                        </div>
                        <span>{t(`customer_groups.groups.members.member_modal.${labelKey}`)}</span>
                      </label>
                    ))}
                    <div className='clear' />
                  </div>
                </div>
              </div>
            </div>

            <div className='row form-group'>
              <div className='col-xs-12'>
                <button
                  type='submit'
                  className='btn btsp_orange_button pull-right'
                  disabled={isLoading}
                >
                  {isLoading
                    ? <i className='fa fa-spinner fa-spin' />
                    : isEdit
                      ? t('customer_groups.groups.members.member_modal.save')
                      : t('customer_groups.groups.members.member_modal.add')
                  }
                </button>
                <button
                  type='button'
                  className='btn btsp_white_button pull-right add-margin-right-5'
                  onClick={onClose}
                  disabled={isLoading}
                >
                  {t('buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )

  return ReactDOM.createPortal(modal, document.body)
}

MemberFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  phoneFieldName: PropTypes.string,
  handicapProvider: PropTypes.string,
  member: PropTypes.shape({
    id: PropTypes.string.isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    gender: PropTypes.string,
    email: PropTypes.string,
    ghin: PropTypes.string,
    phone: PropTypes.string,
    usga_index: PropTypes.string,
  }),
}

MemberFormModal.defaultProps = { member: null }

export default MemberFormModal
